import React from "react";
import {
  AnalyticsSection,
  StatCard,
  BarChart,
  PieChart,
  AreaChart,
} from "./ChartComponents";
import { months, eventData } from "./analyticsData";

const EventsAnalytics = () => {
  return (
    <div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <StatCard
          title="Total Events (Fiscal Year)"
          value={eventData.totalEvents}
        />
        <StatCard title="Completed Events (Fiscal Year)" value={28} />
        <StatCard
          title="Remaining Events (Fiscal Year)"
          value={eventData.upcomingEvents}
        />
        <StatCard
          title="Average Attendance"
          value={eventData.averageAttendance}
        />
        <StatCard
          title="Event Revenue to date (event sponsorship & ticket sales)"
          value={eventData.eventRevenue}
        />
      </div>

      {/* Event Attendance and Event Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <AnalyticsSection title="Event attendance by month">
          <BarChart
            data={eventData.attendanceSeries}
            categories={months}
            height={250}
          />

          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-500">Total Attendance (Year)</p>
              <p className="text-xl font-bold text-cpaGold">
                {eventData.attendanceSeries
                  .reduce((total, series) => {
                    return (
                      total + series.data.reduce((sum, val) => sum + val, 0)
                    );
                  }, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">In-Person %</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  (eventData.attendanceSeries[0].data.reduce(
                    (sum, val) => sum + val,
                    0
                  ) /
                    eventData.attendanceSeries.reduce((total, series) => {
                      return (
                        total + series.data.reduce((sum, val) => sum + val, 0)
                      );
                    }, 0)) *
                    100
                )}
                %
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">Virtual %</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  (eventData.attendanceSeries[1].data.reduce(
                    (sum, val) => sum + val,
                    0
                  ) /
                    eventData.attendanceSeries.reduce((total, series) => {
                      return (
                        total + series.data.reduce((sum, val) => sum + val, 0)
                      );
                    }, 0)) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </AnalyticsSection>

        <AnalyticsSection title="Events by type">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <PieChart
              segments={eventData.eventsByType}
              height={350}
              segmentValueLabel="events"
            />
          </div>
        </AnalyticsSection>
      </div>

      {/* Ticket Sales Trend */}
      <AnalyticsSection title="Ticket sales trend">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">
            Monthly ticket sales across all events
          </p>

          <div className="mt-4">
            <AreaChart
              series={[eventData.ticketSales]}
              labels={months}
              height={250}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Total Tickets Sold</p>
              <p className="text-xl font-bold text-cpaGold">
                {eventData.ticketSales.data
                  .reduce((a, b) => a + b, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Average Monthly</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  eventData.ticketSales.data.reduce((a, b) => a + b, 0) / 12
                ).toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Highest Month</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.max(...eventData.ticketSales.data).toLocaleString()} (Dec)
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Growth (YTD)</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  ((eventData.ticketSales.data[11] -
                    eventData.ticketSales.data[0]) /
                    eventData.ticketSales.data[0]) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      </AnalyticsSection>

      {/* Upcoming Events */}
      <AnalyticsSection title="Upcoming events schedule">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event Name
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registrations
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacity
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                {
                  name: "Business Networking Breakfast",
                  date: "Jun 10, 2023",
                  type: "Networking",
                  registrations: 28,
                  capacity: 50,
                },
                {
                  name: "Digital Marketing Workshop",
                  date: "Jun 17, 2023",
                  type: "Workshop",
                  registrations: 35,
                  capacity: 40,
                },
                {
                  name: "Annual Chamber Luncheon",
                  date: "Jun 24, 2023",
                  type: "Networking",
                  registrations: 65,
                  capacity: 100,
                },
                {
                  name: "Small Business Tax Seminar",
                  date: "Jul 8, 2023",
                  type: "Workshop",
                  registrations: 42,
                  capacity: 50,
                },
                {
                  name: "Summer Business Mixer",
                  date: "Jul 15, 2023",
                  type: "Networking",
                  registrations: 30,
                  capacity: 75,
                },
              ].map((event, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {event.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.registrations}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <span className="mr-2">
                        {event.registrations}/{event.capacity}
                      </span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-cpaGold h-2 rounded-full"
                          style={{
                            width: `${
                              (event.registrations / event.capacity) * 100
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsSection>
    </div>
  );
};

export default EventsAnalytics;
